import React, { useEffect, useState } from "react";
import { Card, CardContent, Box, Button, Tabs, Tab } from "@mui/material";
import * as services from "./../../services/customer";
import * as localServices from "./../../services/local";
import toast, { Toaster } from "react-hot-toast";
import AppBreadcrumbs from "../../components/AppBreadcrumbs";
import PersonalInfoForm from "./personal_Info_form";
import PaymentInfoForm from "./persnal_payment_form";
import { useNavigate, useSearchParams } from "react-router-dom";

const CustomerForm = () => {
  const [searchParams] = useSearchParams();
  const customer_id = searchParams.get("customer_id");

  const [group, setGroup] = useState("");
  const [personType, setPersonType] = useState("normal");

  const [cars, setCars] = useState([]); // { type, license, province }
  const [drivers, setDrivers] = useState([]); // { name, surname, idCard }

  const [tabIndex, setTabIndex] = useState(0);
  const [customerGroups, setCustomerGroups] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    citizenId: "",
    phone: "",
    address: "",
    img: "",
    dob: "",
    // server อาจมีฟิลด์อื่น ๆ ติดมาด้วย แต่เราจะ clean ตอน submit
  });

  const navigate = useNavigate();

  useEffect(() => {
    if (!customer_id) {
      toast.error("ไม่พบ customer_id");
      return;
    }
    loadInitial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer_id]);

  const loadInitial = async () => {
    try {
      // โหลดกลุ่ม
      const resGroups = await services.getGroup();
      const raw = resGroups?.data?.data ?? [];
      setCustomerGroups(raw.map((g) => ({ id: g.id, name: g.name || "-" })));

      // โหลดข้อมูลซัพพลายเออร์
      const resCus = await services.getCustomersById(customer_id);
      const c = resCus?.data?.data;
      console.log("c", c);
      if (!c) throw new Error("ไม่พบข้อมูลซัพพลายเออร์");

      // set form + state
      setFormData({
        name: c.name ?? "",
        citizenId: c.citizenId ?? "",
        phone: c.phone ?? "",
        address: c.address ?? "",
        img: c.img ?? "",
        dob: c.dob ?? "",
        personType: c.personType ?? "normal",
        customerType: c.customerType ?? "SELLER",
        shopId: c.shopId,
        branchId: c.branchId,
      });

      setPersonType(c.personType ?? "normal");
      setGroup(c.customerGroupId);
      setCars(Array.isArray(c.cars) ? c.cars.map(normalizeCar) : []);
      setDrivers(
        Array.isArray(c.drivers) ? c.drivers.map(normalizeDriver) : []
      );
    } catch (e) {
      console.error("loadInitial error:", e);
      toast.error(e?.message ?? "โหลดข้อมูลล้มเหลว");
    }
  };

  // =========== Utils ===========
  const normalizeCar = (car) => ({
    type: car?.type ?? "",
    license: car?.license ?? "",
    province: car?.province ?? "",
  });

  const normalizeDriver = (d) => ({
    name: d?.name ?? "",
    surname: d?.surname ?? d?.suername ?? "", // กันเคสสะกดเดิม
    idCard: d?.idCard ?? "",
  });

  const isBase64 = (str) =>
    typeof str === "string" && /^data:image\/[a-zA-Z]+;base64,/.test(str);

  const base64ToBlob = (base64, mimeType = "image/png") => {
    const byteString = atob(base64.split(",")[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++)
      ia[i] = byteString.charCodeAt(i);
    return new Blob([ab], { type: mimeType });
  };

  // =========== Reader ===========
  const readCard = async () => {
    try {
      const res = await localServices.readCard();
      const data = res.data.data;
      setFormData((prev) => ({
        ...prev,
        name: `${data.firstNameTH} ${data.lastNameTH}`,
        dob: data.birthday,
        citizenId: data.citizenId,
        address: data.address,
        img: data.photo,
      }));
    } catch (err) {
      console.error("readCard err:", err);
      alert("ไม่สามารถอ่านบัตรได้");
    }
  };

  // =========== Form handlers ===========
  const handleChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleTabChange = (_, newValue) => setTabIndex(newValue);

  // Cars
  const handleAddCar = (car) => {
    if (car && typeof car === "object") {
      setCars((prev) => [...prev, normalizeCar(car)]);
    } else {
      setCars((prev) => [...prev, { type: "", license: "", province: "" }]);
    }
  };
  const handleDeleteCar = (index) => {
    setCars((prev) => prev.filter((_, i) => i !== index));
  };
  const handleCarChange = (index, field, value) => {
    setCars((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  // Drivers
  const handleAddDriver = (driver) => {
    if (driver && typeof driver === "object") {
      setDrivers((prev) => [...prev, normalizeDriver(driver)]);
    } else {
      setDrivers((prev) => [...prev, { name: "", surname: "", idCard: "" }]);
    }
  };
  const handleDeleteDriver = (index) => {
    setDrivers((prev) => prev.filter((_, i) => i !== index));
  };
  const handleDriverChange = (index, field, value) => {
    setDrivers((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  // =========== Submit ===========
  const handleSubmit = async () => {
    try {
      let imgToSend = formData.img ?? "";

      // อัปโหลดรูปเมื่อเป็น base64
      if (imgToSend && isBase64(imgToSend)) {
        const blob = base64ToBlob(imgToSend);
        const form = new FormData();
        form.append("file", blob, "image.png");
        const response = await services.upload(form);
        imgToSend = response?.data?.file ?? "";
      }

      // clean ฟิลด์ที่ไม่ควรส่งกลับ (เหลือแต่ที่แก้ได้)
      // NOTE: ถ้า formData ดึงมาทั้งก้อนจาก server จะมี id/createdAt/updatedAt เป็นต้น
      const {
        id,
        createdAt,
        updatedAt,
        deletedAt,
        userId,
        user,
        cars: _cars,
        drivers: _drivers,
        customerGroup, // relation object
        ...clean
      } = formData;

      // แปลง null -> undefined (Elysia schema จะชอบ undefined มากกว่า null)
      const scrubNulls = (obj) =>
        Object.fromEntries(
          Object.entries(obj).map(([k, v]) => [k, v === null ? undefined : v])
        );

      const payload = scrubNulls({
        ...clean,
        img: imgToSend,
        personType,
        customerGroupId: group,
        customerType: clean.customerType ?? "SELLER",
        cars,
        drivers,
      });

      await services.updateCustomer(payload, customer_id);
      toast.success("แก้ไขรายการสำเร็จ!");
      // รีโหลดข้อมูลเพื่อ sync state ล่าสุด (หรือจะ navigate กลับ list ก็ได้)
      await loadInitial();
    } catch (err) {
      console.error("update error:", err);
      const msg =
        err?.response?.data?.error ?? err?.message ?? "แก้ไขรายการไม่สำเร็จ!";
      toast.error(msg);
    }
  };

  return (
    <Box p={2}>
      <AppBreadcrumbs />
      <Toaster position="bottom-right" reverseOrder={false} />

      <Box sx={{ maxWidth: 1000, mx: "auto", mt: 4 }}>
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Tabs value={tabIndex} onChange={handleTabChange} centered>
              <Tab label="ข้อมูลส่วนตัว" />
              <Tab label="การชำระเงิน" />
            </Tabs>

            {tabIndex === 0 && (
              <Box sx={{ mt: 2 }}>
                <PersonalInfoForm
                  formData={formData}
                  handleChange={handleChange}
                  readCard={readCard}
                  customerGroups={customerGroups}
                  group={group}
                  setGroup={setGroup}
                  personType={personType}
                  setPersonType={setPersonType}
                  // ✅ ต่อพร็อปของรถ/คนขับให้ครบ เพื่อให้ Dialog ในฟอร์มทำงานได้
                  cars={cars}
                  drivers={drivers}
                  handleAddCar={handleAddCar}
                  handleAddDriver={handleAddDriver}
                  handleDeleteCar={handleDeleteCar}
                  handleDeleteDriver={handleDeleteDriver}
                  handleCarChange={handleCarChange}
                  handleDriverChange={handleDriverChange}
                />
              </Box>
            )}

            {tabIndex === 1 && (
              <Box sx={{ mt: 2 }}>
                <PaymentInfoForm
                  formData={formData}
                  handleChange={handleChange}
                  readCard={readCard}
                  customerGroups={customerGroups}
                  group={group}
                  setGroup={setGroup}
                  personType={personType}
                  setPersonType={setPersonType}
                  // ถ้าฟอร์มการชำระเงินต้องเห็นรถ/แก้ไขด้วย ก็ส่งให้เหมือนกัน
                  cars={cars}
                  handleAddCar={handleAddCar}
                  handleDeleteCar={handleDeleteCar}
                  handleCarChange={handleCarChange}
                  // กันกรณีคอมโพเนนต์เก่าเรียก props ผิดสะกด
                  handleDriverChange={handleDriverChange}
                  handleDriverChang={handleDriverChange}
                />
              </Box>
            )}

            <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
              >
                บันทึกข้อมูล
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default CustomerForm;
